import { Map as MapIcon, AutoAwesome as Sparkles, Upload } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link } from '@tanstack/react-router';
import { DragEvent, useRef, useState, useEffect } from 'react';

import type { Location } from '@/lib/types';

import { useFirebase } from '@/firebase';
import { deleteStorageFile, uploadImage } from '@/firebase/storage';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { MapEntityOverlay } from './MapEntityOverlay';
import {
    buildMapImageUploadUpdates,
    getReplacedMapStoragePaths,
    type MapImageUploadUpdates,
} from './map-image-upload';
import { SecureImage } from '@/components/secure-image';



interface LocationMapManagerProps {
    location: Location;
    children?: React.ReactNode;
    onDragEnter: (e: DragEvent<HTMLDivElement>) => void;
    onDragLeave: (e: DragEvent<HTMLDivElement>) => void;
    onDragOver: (e: DragEvent<HTMLDivElement>) => void;
    onDrop: (e: DragEvent<HTMLDivElement>) => void;
    isDraggingPin: boolean;
    readonly?: boolean;
    disabled?: boolean;
    onMapImageUpdated?: (updates: MapImageUploadUpdates) => void;
    onUploadingChange?: (isUploading: boolean) => void;
}

export function LocationMapManager({
    location,
    children,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
    isDraggingPin,
    readonly = false,
    disabled = false,
    onMapImageUpdated,
    onUploadingChange,
}: LocationMapManagerProps) {
    const { t } = useTranslation();
    const { storage, auth } = useFirebase();
    const { updateEntityItem, selectedNovelId } = useNovelRepository();
    const { toast } = useToast();
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [aspectRatio, setAspectRatio] = useState<number | string>('16/9');
    const imageContainerRef = useRef<HTMLDivElement>(null);
    const [imgEl, setImgEl] = useState<HTMLImageElement | null>(null);
    const [imageRenderDims, setImageRenderDims] = useState({ w: 0, h: 0, l: 0, t: 0, nw: 0, nh: 0 });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        if (!location.mapImageUrl) {
            setAspectRatio('16/9');
        }
    }, [location.mapImageUrl]);

    useEffect(() => {
        const container = imageContainerRef.current;
        if (!container || !imgEl) return;

        const observer = new ResizeObserver(() => {
            const cr = container.getBoundingClientRect();
            const ir = imgEl.getBoundingClientRect();
            const nw = imgEl.naturalWidth || 1024;
            const nh = imgEl.naturalHeight || 1024;
            setImageRenderDims({
                w: ir.width,
                h: ir.height,
                l: ir.left - cr.left,
                t: ir.top - cr.top,
                nw,
                nh,
            });
        });

        observer.observe(imgEl);
        observer.observe(container);

        // trigger initially
        const cr = container.getBoundingClientRect();
        const ir = imgEl.getBoundingClientRect();
        setImageRenderDims({
            w: ir.width,
            h: ir.height,
            l: ir.left - cr.left,
            t: ir.top - cr.top,
            nw: imgEl.naturalWidth || 1024,
            nh: imgEl.naturalHeight || 1024,
        });

        return () => {
            observer.disconnect();
        };
    }, [imgEl]);

    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        setImgEl(img);
        const { naturalWidth, naturalHeight } = img;
        if (naturalWidth && naturalHeight) {
            setAspectRatio(`${naturalWidth}/${naturalHeight}`);
        }
    };

    const handleFileUpload = async (file: File) => {
        if (disabled || !storage || !auth || !selectedNovelId) return;
        setIsUploading(true);
        onUploadingChange?.(true);

        try {
            // Convert to WebP on the client side before upload
            const webpFile = await new Promise<File>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const img = new Image();
                    img.onload = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');
                        if (!ctx) {
                            reject(new Error("Failed to create canvas context"));
                            return;
                        }
                        ctx.drawImage(img, 0, 0);
                        canvas.toBlob((blob) => {
                            if (blob) {
                                const newName = file.name.replace(/\.[^/.]+$/, "") + ".webp";
                                resolve(new File([blob], newName, { type: "image/webp" }));
                            } else {
                                reject(new Error("Conversion to WebP failed"));
                            }
                        }, 'image/webp', 0.90);
                    };
                    img.onerror = () => reject(new Error("Failed to load image"));
                    img.src = e.target?.result as string;
                };
                reader.onerror = () => reject(new Error("Failed to read file"));
                reader.readAsDataURL(file);
            });

            const imageRef = await uploadImage(storage, auth, webpFile, selectedNovelId, `locations/${location.id}/map`);
            const mapImageUpdates = buildMapImageUploadUpdates(location, imageRef);
            const pathsToDelete = getReplacedMapStoragePaths(location, imageRef.path);

            await updateEntityItem('locations', location.id, { ...mapImageUpdates });
            onMapImageUpdated?.(mapImageUpdates);

            // Clean up former files in storage asynchronously
            await Promise.all(pathsToDelete.map(path => deleteStorageFile(storage, path)));

            toast({
                title: 'Upload Successful',
                description: `Map image has been updated.`,
            });
        } catch (error) {
            console.error("Upload error:", error);
            toast({
                title: 'Upload Failed',
                description: 'Could not upload map image.',
                variant: 'destructive',
            });
        } finally {
            setIsUploading(false);
            onUploadingChange?.(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleFileSelection = (files: FileList | null) => {
        if (files && files.length > 0) {
            const file = files[0];
            if (location.mapImageUrl) {
                setPendingFile(file);
                setIsConfirmOpen(true);
            } else {
                handleFileUpload(file);
            }
        }
    };

    const handleConfirmUpload = () => {
        setIsConfirmOpen(false);
        if (pendingFile) {
            handleFileUpload(pendingFile);
            setPendingFile(null);
        }
    };

    const handleCancelUpload = () => {
        setIsConfirmOpen(false);
        setPendingFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <Paper
            elevation={4}
            onDragEnter={readonly ? undefined : onDragEnter}
            onDragLeave={readonly ? undefined : onDragLeave}
            onDragOver={readonly ? undefined : onDragOver}
            onDrop={readonly ? undefined : onDrop}
            sx={{
                position: 'relative',
                overflow: 'visible',
                borderRadius: 3,
                bgcolor: 'grey.900',
                transition: 'all 0.3s ease',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
                }
            }}
        >
            <Box sx={{ position: 'relative', width: '100%', aspectRatio, maxHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {(isUploading) && (
                    <Box sx={{ position: 'absolute', inset: 0, zIndex: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)', flexDirection: 'column', gap: 2 }}>
                        <CircularProgress color="primary" />
                        <Typography variant="h6" color="white" fontWeight="bold">Uploading Map...</Typography>
                    </Box>
                )}
                {isDraggingPin && (
                    <Box sx={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(25, 118, 210, 0.1)', border: '3px dashed', borderColor: 'primary.main', borderRadius: 2, pointerEvents: 'none' }}>
                        <Typography variant="h5" color="primary.main" fontWeight="bold" sx={{ bgcolor: 'background.paper', px: 3, py: 1, borderRadius: 2, boxShadow: 3 }}>Drop Location Pin</Typography>
                    </Box>
                )}

                {location.mapImageUrl ? (
                    <Box ref={imageContainerRef} sx={{ width: '100%', height: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.5))' }}>
                        <SecureImage
                            src={location.mapImageUrl}
                            path={location.mapImageStoragePath ?? undefined}
                            alt={`${location.name} map`}
                            onLoad={handleImageLoad}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                width: 'auto',
                                height: 'auto',
                                display: 'block',
                            }}
                        />
                        {imageRenderDims.w > 0 && (
                            <MapEntityOverlay
                                location={location}
                                imageWidth={imageRenderDims.nw}
                                imageHeight={imageRenderDims.nh}
                                renderWidth={imageRenderDims.w}
                                renderHeight={imageRenderDims.h}
                                offsetLeft={imageRenderDims.l}
                                offsetTop={imageRenderDims.t}
                            />
                        )}
                        {!readonly && (
                            <Stack direction="row" spacing={1} sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
                                {(location.mapImageUrl.includes('map_color') || location.mapImageUrl.includes('color.png')) && (
                                    <Tooltip title="This preview is missing layers. Save it in the Map Creator to update.">
                                        <Chip label="Legacy Preview" color="warning" size="small" variant="filled" sx={{ fontWeight: 'bold' }} />
                                    </Tooltip>
                                )}
                                <Box sx={{ display: 'flex', gap: 1, backdropFilter: 'blur(8px)', bgcolor: 'rgba(255,255,255,0.05)', p: 0.5, borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="inherit"
                                        disabled={disabled || isUploading}
                                        onClick={() => fileInputRef.current?.click()}
                                        sx={{
                                            minWidth: 0,
                                            bgcolor: 'rgba(255,255,255,0.9)',
                                            color: 'black',
                                            '&:hover': { bgcolor: 'white' },
                                            fontSize: '0.7rem',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        <Upload sx={{ fontSize: 14, mr: 0.5 }} />
                                        {t('common.change')}
                                    </Button>
                                    <Button
                                        component={isMobile ? 'button' : Link}
                                        to={isMobile ? undefined : `/locations/map/${location.id}`}
                                        onClick={(e: React.MouseEvent) => {
                                            if (isMobile) {
                                                e.preventDefault();
                                                toast({
                                                    title: 'Wider Screen Required',
                                                    description: 'The map editor requires a wider screen to be used effectively.',
                                                    variant: 'destructive',
                                                });
                                            }
                                        }}
                                        variant="contained"
                                        size="small"
                                        sx={{
                                            fontSize: '0.7rem',
                                            fontWeight: 'bold',
                                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                                        }}
                                    >
                                        <Sparkles sx={{ fontSize: 14, mr: 0.5 }} />
                                        {t('common.edit_map')}
                                    </Button>
                                </Box>
                            </Stack>
                        )}
                        {children}
                    </Box>
                ) : (
                    <Stack justifyContent="center" alignItems="center" height="100%" color="text.secondary" spacing={3} p={4} sx={{ background: 'radial-gradient(circle at center, #1a1a1a 0%, #000 100%)' }}>
                        <Box sx={{ position: 'relative' }}>
                            <MapIcon sx={{ fontSize: 80 }} />
                            <Sparkles sx={{ fontSize: 24, position: 'absolute', top: -10, right: -10 }} />
                        </Box>
                        <Box textAlign="center">
                            <Typography variant="h5" color="white" fontWeight="bold" gutterBottom>{t('map_creator.forge_title')}</Typography>
                            <Typography variant="body2" color="grey.500" sx={{ maxWidth: 300 }}>
                                {t('map_creator.forge_desc')}
                            </Typography>
                        </Box>
                        {!readonly && (
                            <Stack direction="row" spacing={2} width="100%" maxWidth={400}>
                                <Button variant="outlined" fullWidth disabled={disabled || isUploading} onClick={() => fileInputRef.current?.click()} sx={{ borderColor: 'grey.800', color: 'grey.400' }} startIcon={<Upload sx={{ fontSize: 16 }} />}>
                                    {t('common.upload')}
                                </Button>
                                <Button 
                                    component={isMobile ? 'button' : Link} 
                                    to={isMobile ? undefined : `/locations/map/${location.id}`} 
                                    onClick={(e: React.MouseEvent) => {
                                        if (isMobile) {
                                            e.preventDefault();
                                            toast({
                                                title: 'Wider Screen Required',
                                                description: 'The map editor requires a wider screen to be used effectively.',
                                                variant: 'destructive',
                                            });
                                        }
                                    }}
                                    variant="contained" 
                                    fullWidth 
                                    startIcon={<Sparkles sx={{ fontSize: 16 }} />}
                                >
                                    {t('map_creator.create_map')}
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                )}
            </Box>
            {!readonly && (
                <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={(e) => handleFileSelection(e.target.files)} />
            )}
            <Dialog
                open={isConfirmOpen}
                onClose={handleCancelUpload}
                aria-labelledby="confirm-map-overwrite-title"
                aria-describedby="confirm-map-overwrite-description"
            >
                <DialogTitle id="confirm-map-overwrite-title">
                    {t('map_creator.confirm_overwrite_title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-map-overwrite-description">
                        {t('map_creator.confirm_overwrite_desc')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelUpload} color="inherit">
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={handleConfirmUpload} color="error" variant="contained" autoFocus>
                        {t('map_creator.overwrite')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
